import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getCookie, resetTokensAndSendToLogin, setCookie } from 'helpers/cookies';
import users from './users';
import { IQesSigningContext } from 'interfaces';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';
import { refresTokensHandle } from 'helpers/tokens';

const correctHeaders = window.location?.href?.includes('localhost:3000')
  ? { "x-forwarded-for": "0.0.0.0", "Access-Control-Allow-Origin": "*"}
  : { "Access-Control-Allow-Origin": "*" };

const signatureTemplate = axios.create({
  baseURL: window.REACT_APP_SIGNATURE_API !== 'REPLACE_REACT_APP_SIGNATURE_API' ? window.REACT_APP_SIGNATURE_API : process.env.REACT_APP_SIGNATURE_API || 'http://localhost:8060/api',
  headers: correctHeaders,
});

signatureTemplate.interceptors.request.use(async(config: InternalAxiosRequestConfig) => {
  let accessToken = getCookie('athena-token');
  let decodeInfo: any;
  if (accessToken) {
    try {
      decodeInfo = jwtDecode(accessToken);
    } catch (error) {
      Promise.reject('Error decoding token');
    }
    const expTime = decodeInfo?.exp && decodeInfo.exp * 1000;
    const curTime = new Date().getTime();

    if (expTime && expTime - curTime <= 50000) {
      const refresh_token = getCookie('refresh_token');      
      if (refresh_token) {
        try {
          const newTokens = await refresTokensHandle(refresh_token);
          accessToken = newTokens.access_token;
        } catch (error) {
          Promise.reject('Error refreshing tokens');
          resetTokensAndSendToLogin();
        };
      };
    };

    config.headers!.Authorization = `Bearer ${accessToken}`
  } else {
    setCookie('athena-token', '', 1);
  }
  return config;
});

let cntTryingUpdateFingerprint = 0;

signatureTemplate.interceptors.response.use(( response: AxiosResponse ) => {
  return response
}, async (error: AxiosError<any>) => {
  if (error.response?.status === 401 && cntTryingUpdateFingerprint < 1) {
    cntTryingUpdateFingerprint++;

    try {
      const formData = new FormData();
      const browserData = await functionCollectBrowserData();
      formData.append('browser_data', JSON.stringify(browserData) || '');

      const freshTokens = await users.updateFingerprint(formData);
      setCookie('athena-token', freshTokens.data.access_token, 1);
      setCookie('refresh_token', freshTokens.data.refresh_token, 1);

      return signatureTemplate.request(error.request.responseURL);
    } catch {
      resetTokensAndSendToLogin();
    }
  } else if (cntTryingUpdateFingerprint >= 1) {
    resetTokensAndSendToLogin();
  };
  return Promise.reject(error)
});

// QES Signature API
const getSigningContext = (documentId: string) => 
  signatureTemplate.get<IQesSigningContext>(`/qes/signatures/${documentId}/signing-context/`);

const getLatestSignature = (documentId: string) => 
  signatureTemplate.get<string>(`/qes/signatures/${documentId}/latest-signature/`);

const addSignature = (documentId: string, signatureData: Blob) => {
  const formData = new FormData();
  formData.append('signature', signatureData, 'signature.p7s');
  return signatureTemplate.post(`/qes/signatures/${documentId}/add-signature/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

const signature = {
  getSigningContext,
  getLatestSignature,
  addSignature,
};

export default signature;

